import { offlineAlbums, offlineSongs, offlineTracks } from "@sqlite/schema";
import { OfflineDownloadStatus } from "@sqlite/constants";
import { inArray } from "drizzle-orm";
import type { MobileDatabase } from "../../../runtime/ports";
import {
  getOfflineTrackPath,
  reconstructImagePath,
} from "@utils/downloadManager/utils";
import type { Song } from "@libs/wire";

/**
 * One-shot read of every locally-stored asset for a set of song ids:
 *   • audio (offline_tracks: only when status === COMPLETED)
 *   • artist images (offline_songs.artistImages, parallel to artistIds)
 *   • album image    (offline_albums.image, joined via offline_songs.albumId)
 *   • setlist        (offline_songs.setList)
 *
 * Audio gating differs from image gating intentionally: audio is gated
 * on "download completed" because a partially-downloaded file isn't
 * playable, whereas images are gated on "we have a row at all" because
 * image download is its own queue and the row's existence implies the
 * image file was queued and the path repair (reconstructImagePath) keeps
 * it valid across iOS GUID drift.
 *
 * The query is intentionally synchronous (drizzle expo-sqlite + `.all()`)
 * so callers can compose it inline with `withDurableCache` / queue
 * resolution without an extra await round-trip; on web the mocked
 * `db.select` returns `[]`, which trivially yields an empty map.
 */
export type LocalSongAssets = {
  /** file:// path to the local audio file. Undefined if not yet COMPLETED. */
  audioUrl?: string;
  /** Map of artistId → file:// path. Missing entries fall back to remote. */
  artistImageById: Map<string, string>;
  /** Map of albumId → file:// path. Missing entries fall back to remote. */
  albumImageById: Map<string, string>;
  /** Offline setlist metadata for UI panels. Undefined for non-setlists. */
  setList?: Song["setList"];
};

export function createLocalSongAssets(db: MobileDatabase) {
  function readLocalSongAssets(
    songIds: string[],
  ): Map<string, LocalSongAssets> {
    if (songIds.length === 0) return new Map();
    const out = new Map<string, LocalSongAssets>();
    try {
      // 1. Tracks → audio status
      const trackRows = db
        .select({
          trackId: offlineTracks.trackId,
          status: offlineTracks.audioDownloadStatus,
        })
        .from(offlineTracks)
        .where(inArray(offlineTracks.trackId, songIds))
        .all();

      // 2. Songs → artistIds + artistImages + albumId
      const songRows = db
        .select({
          id: offlineSongs.id,
          artistIds: offlineSongs.artistIds,
          artistImages: offlineSongs.artistImages,
          albumId: offlineSongs.albumId,
          setList: offlineSongs.setList,
        })
        .from(offlineSongs)
        .where(inArray(offlineSongs.id, songIds))
        .all();

      // 3. Albums → album image (one batched read)
      const albumIds = Array.from(
        new Set(songRows.map((row) => row.albumId).filter(Boolean)),
      );
      const albumImageById = new Map<string, string>();
      if (albumIds.length > 0) {
        const albumRows = db
          .select({
            id: offlineAlbums.id,
            image: offlineAlbums.image,
          })
          .from(offlineAlbums)
          .where(inArray(offlineAlbums.id, albumIds))
          .all();
        for (const row of albumRows) {
          const path = reconstructImagePath(row.image);
          if (path) albumImageById.set(row.id, path);
        }
      }

      // 4. Audio path index
      const completedAudio = new Map<string, string>();
      for (const row of trackRows) {
        if (row.status === OfflineDownloadStatus.COMPLETED) {
          completedAudio.set(row.trackId, getOfflineTrackPath(row.trackId));
        }
      }

      // 5. Compose per-song assets
      for (const song of songRows) {
        const artistImageById = new Map<string, string>();
        for (let i = 0; i < song.artistIds.length; i++) {
          const stored = song.artistImages[i];
          const path = reconstructImagePath(stored);
          if (path) artistImageById.set(song.artistIds[i], path);
        }
        const songAlbumImage = song.albumId
          ? albumImageById.get(song.albumId)
          : undefined;
        const albumIndex = new Map<string, string>();
        if (song.albumId && songAlbumImage) {
          albumIndex.set(song.albumId, songAlbumImage);
        }
        out.set(song.id, {
          audioUrl: completedAudio.get(song.id),
          artistImageById,
          albumImageById: albumIndex,
          setList: song.setList ?? undefined,
        });
      }

      // 6. Songs with completed audio but no offline_songs row (rare —
      //    can happen if metadata is missing). Surface audio anyway.
      for (const [songId, audioUrl] of completedAudio) {
        if (!out.has(songId)) {
          out.set(songId, {
            audioUrl,
            artistImageById: new Map(),
            albumImageById: new Map(),
          });
        }
      }
    } catch (err) {
      // Disk hiccup or schema mismatch — never break playback over a local
      // optimization. Caller falls back to whatever the song carried.
      console.audio?.warn?.("readLocalSongAssets failed", { error: err });
    }
    return out;
  }

  /**
   * Substitute the locally-downloaded asset paths into songs:
   *   • urlOnline (audio)             — gated on offline_tracks COMPLETED
   *   • artists[i].image (per-artist) — gated on offline_songs row exists
   *   • albums[i].image (per-album)   — gated on offline_albums row exists
   *   • setList / isSetList           — gated on offline_songs.setList exists
   *
   * Server is authoritative on what's IN the list — we only field-substitute
   * paths the device knows about. Idempotent: substituting twice produces
   * the same result (already-local file:// inputs aren't in cached_tracks
   * to begin with).
   */
  function substituteLocalSongAssets<
    T extends Pick<Song, "id" | "urlOnline" | "artists" | "albums"> &
      Partial<Pick<Song, "isSetList" | "setList">>,
  >(songs: T[]): T[] {
    if (songs.length === 0) return songs;
    const local = readLocalSongAssets(songs.map((s) => s.id));
    if (local.size === 0) return songs;

    return songs.map((song) => {
      const assets = local.get(song.id);
      if (!assets) return song;

      let next = song;
      let mutated = false;

      if (assets.audioUrl) {
        next = { ...next, urlOnline: assets.audioUrl };
        mutated = true;
      }

      if (assets.artistImageById.size > 0) {
        const newArtists = song.artists.map((artist) => {
          const local = assets.artistImageById.get(artist.id);
          return local ? { ...artist, image: local } : artist;
        });
        // Only allocate a new array if at least one artist actually changed.
        if (newArtists.some((a, i) => a !== song.artists[i])) {
          next = { ...next, artists: newArtists };
          mutated = true;
        }
      }

      if (assets.albumImageById.size > 0) {
        const newAlbums = song.albums.map((album) => {
          const local = assets.albumImageById.get(album.id);
          return local ? { ...album, image: local } : album;
        });
        if (newAlbums.some((a, i) => a !== song.albums[i])) {
          next = { ...next, albums: newAlbums };
          mutated = true;
        }
      }

      if (assets.setList?.entries?.length) {
        next = { ...next, isSetList: true, setList: assets.setList };
        mutated = true;
      }

      return mutated ? next : song;
    });
  }

  return { readLocalSongAssets, substituteLocalSongAssets };
}
